import {inject, Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {CustomDatesService} from "./custom-dates.service";
import {AiAlertAnomalyType} from "../../feature-modules/alerts-module/alert-list/_models/AiAlertAnomalyType";
import {AiAlertAnomalySeverity} from "../../feature-modules/alerts-module/alert-list/_models/AiAlertAnomalySeverity";
import {Line} from "../interfaces/rest/Line";
import {Asset} from "../interfaces/rest/Asset";
import {HttpRepository} from "../repositories/http.repository";
import moment from "moment";
import { unitTransTokens, unitValues } from "../constants/momentUnitMeasurement";
import {AlertData} from "../interfaces/domain/alert/AlertData";
import {forkJoin, Observable, of} from "rxjs";
import {switchMap} from "rxjs/operators";
import {AlertComment} from "../interfaces/rest/AlertComment";
import {AlertFeedback} from "../interfaces/rest/AlertFeedback";
import {Remedy} from "../interfaces/rest/Remedy";
import {User} from "../interfaces/rest/User";
import {AlertCommentRepository} from "../repositories/alert-comment.repository";
import {AlertFeedbackRepository} from "../repositories/alert-feedback.repository";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {RemedyRepository} from "../repositories/remedy.repository";
import {AssetRepository} from "../repositories/asset.repository";
import {LineRepository} from "../repositories/line.repository";
import {UserRepository} from "../repositories/user.repository";
import {AlertDataResponse} from "../interfaces/rest/AlertDataResponse";
import {Helper} from "../helpers/Helper";
import {AlertStatus} from "../interfaces/domain/alert/AlertStatus";

@Injectable()
export class AlertService {

  private readonly alertCommentRepository= inject(AlertCommentRepository);
  private readonly alertFeedbackRepository = inject(AlertFeedbackRepository);
  private readonly userSessionRepository = inject(UserSessionRepository);
  private readonly remedyRepository = inject(RemedyRepository);
  private readonly assetRepository = inject(AssetRepository);
  private readonly lineRepository = inject(LineRepository);
  private readonly userRepository = inject(UserRepository);

  private readonly customDatesService = inject(CustomDatesService);

  private readonly customTranslateService = inject(TranslateService);
  unitTransTokens = unitTransTokens;
  unitValues = unitValues;

  private stateOpenTranslation = 'Open';
  private stateAcknowledgedTranslation = 'Acknowledged';
  private stateClosedTranslation = 'Closed';
  private aiAlertAnomalyTypeCumulativeTranslation = 'TYPE_CUMULATIVE_DURATION';
  private aiAlertAnomalyTypeInstanceTranslation = 'TYPE_INSTANCE_COUNT';
  private aiAlertAnomalyTypeInstanceDuration = 'TYPE_INSTANCE_DURATION';
  private aiAlertCausedByLabelTranslation = 'Caused by';
  private aiAlertAnomalySeverityUnKnown = 'SEVERITY_UNKNOWN';
  private aiAlertAnomalySeverityNone = 'SEVERITY_NONE';
  private aiAlertAnomalySeverityLow = 'SEVERITY_LOW';
  private aiAlertAnomalySeverityMedium = 'SEVERITY_MEDIUM';
  private aiAlertAnomalySeverityHigh = 'SEVERITY_HIGH';
  private aiAlertAnomalySeveritySevere = 'SEVERITY_SEVERE';
  private alertRuleTypeTranslationInitialized = false;
  private alertRuleTypeValueStandard = "STANDARD";
  private alertRuleTypeValueAi = "AI";
  private aiAlertAnomalyTypeTranslationInitialized = false;
  private aiAlertAnomalySeverityTranslationInitialized =false;
  private translationInitialized = false;

  private targetDatetimeFormat = 'MM/DD/YYYY HH:mm';

  constructor(protected httpRepository: HttpRepository) {
    this.targetDatetimeFormat = this.customDatesService.getUserDateTimeFormat();
  }

  protected getLangCode(): string {
    const userSession = this.userSessionRepository.getUserSession();

    if(userSession) {
      return userSession.languageCode || 'en';
    }

    return 'en';
  }

  createAlertDataFromHistoryResponse(alert: any): AlertData {
    const langCode = this.getLangCode();

    const alertData: AlertData = new AlertData();
    alertData.isRealAlert = true;
    alertData.alertRuleId = alert.AlertRuleId;
    alertData.alertInstanceId = alert.AlertInstanceId;
    alertData.name = alert.AlertName;
    alertData.description = Helper.getTranslationFromJsonString(alert.AlertDescription, langCode);
    alertData.alertRuleType = alert.AlertRuleType;

    if(alert.AlertRuleType === 'AI') {
      alertData.description = this.getUpdateAIAlertDescription(alertData.description!, alert.childAnomalies);
    }

    alertData.title = Helper.getTranslationFromJsonString(alert.Title, langCode);
    alertData.lineId = alert.LineId;
    alertData.asset = alert.AssetName;
    alertData.openDatetimeMillis = this.customDatesService.getTimestampInMillis(alert.OpenTimestamp);
    alertData.closeDatetimeMillis = this.customDatesService.getTimestampInMillis(alert.CloseTimestamp);
    alertData.ackDatetimeMillis = this.customDatesService.getTimestampInMillis(alert.AckTimestamp);
    alertData.openDatetime = this.customDatesService.convertMillisToUserFormat(alertData.openDatetimeMillis);
    alertData.status = alert.AlertState;
    alertData.ackDatetime = (alert.AckTimestamp !== 'NA') ? this.customDatesService.convertMillisToUserFormat(alertData.ackDatetimeMillis) : alert.AckTimestamp;
    alertData.ackBy = alert.AckBy;
    alertData.ackComment = alert.AckComment;
    alertData.client = alert.ClientId;

    return alertData;
  }

  createAlertDataFromResponse(data: AlertDataResponse): AlertData {
    const alertData = new AlertData();
    const langCode = this.getLangCode();

    alertData.title = data.title;
    alertData.alertInstanceId = data.alertInstanceId.toString();
    alertData.alertRuleId = data.alertRuleId;
    alertData.isRealAlert = true;
    alertData.status = data.status;
    alertData.ackBy = data.ackBy;
    alertData.ackComment = data.ackComment;
    alertData.ackDatetime = this.formatTwAlertTimestamp(data.ackDatetime);
    alertData.closeDatetime = this.formatTwAlertTimestamp(data.closeDatetime);
    alertData.openDatetime = this.formatTwAlertTimestamp(data.openDatetime);
    alertData.ackDatetimeMillis = this.getTwAlertTimestampInMillis(data.ackDatetime);
    alertData.closeDatetimeMillis = this.getTwAlertTimestampInMillis(data.closeDatetime);
    alertData.openDatetimeMillis = this.getTwAlertTimestampInMillis(data.openDatetime);
    alertData.name = data.name;
    alertData.childAnomalies = data.childAnomalies;
    alertData.alertRuleType = data.alertRuleType;
    alertData.description = Helper.checkJsonString(data.description) ? Helper.getTranslationFromJson(JSON.parse(data.description), langCode) : data.description;
    alertData.title = Helper.checkJsonString(data.title) ? Helper.getTranslationFromJson(JSON.parse(data.title), langCode) : data.title;
    alertData.title = alertData.title ? alertData.title : data.name;
    alertData.conditions = data.conditions;
    alertData.lineId = data.lineId;
    alertData.asset = data.asset;
    alertData.client = data.clientId;

    alertData.openDuration = this.calculateDuration(2, alertData.openDatetime!);
    if (alertData.ackDatetime) {
      alertData.ackDuration = this.calculateDuration(2, alertData.openDatetime!, alertData.ackDatetime);
    }
    if (alertData.closeDatetime) {
      alertData.closeDuration = this.calculateDuration(2, alertData.openDatetime!, alertData.closeDatetime);
    }

    alertData.populateSource();

    return alertData;
  }

  protected formatTwAlertTimestamp(timestamp: string) {
    if(!timestamp || timestamp === 'NA') {
      return undefined;
    }

    return moment.utc(timestamp).local().format(this.targetDatetimeFormat);
  }

  protected getTwAlertTimestampInMillis(timestamp: string) {
    if(!timestamp || timestamp === 'NA') {
      return undefined;
    }

    return moment.utc(timestamp).local().valueOf();
  }

  getAlertRuleTypeTranslation(alertRuleTypeValue : string) {
    if(!this.alertRuleTypeTranslationInitialized) {
      this.customTranslateService.get('alertRuleTypeValueAi', (message : string) => {
        this.alertRuleTypeValueAi = message ? message : alertRuleTypeValue;
      });
      this.customTranslateService.get('alertRuleTypeValueStandard', (message : string) => {
        this.alertRuleTypeValueStandard =  message ? message : alertRuleTypeValue;
      });

      this.alertRuleTypeTranslationInitialized = true;
    }

    switch (alertRuleTypeValue.trim()) {
      case 'AI':
        return this.alertRuleTypeValueAi;
      case 'STANDARD':
        return this.alertRuleTypeValueStandard;
      default:
        return 'NA';
    }
  }

  getUpdateAIAlertDescription(alertDescription: string, childAnomalies: string) {
    if (childAnomalies) {
      const anomalies = childAnomalies.split('|');
      const translatedAnomalies = anomalies.map(anomaly => {
        let [anomalyTypeName, childSeverityType] = anomaly.split('-');
        return  (this.getAiAlertAnomalyTypeTranslation(anomalyTypeName).concat(' (').concat(this.getAiAlertChildAnomalySeverityTypeTranslation(childSeverityType)).concat(')')) ;
      });
      let aiAlertCausedByLabelTranslated = this.aiAlertCausedByLabelTranslation;
      this.customTranslateService.get('aiAlertNotificationCausedByLabel').subscribe({
        next: (message) => {
          aiAlertCausedByLabelTranslated  = message ? message : aiAlertCausedByLabelTranslated ;
        }
      });
      if (alertDescription.indexOf(aiAlertCausedByLabelTranslated) > -1) {
        alertDescription = alertDescription.substring(0, alertDescription.indexOf(aiAlertCausedByLabelTranslated)).trim();
      }

      alertDescription = alertDescription + '\n\n' + aiAlertCausedByLabelTranslated + ': ' + translatedAnomalies.join(', ');
    }
    return alertDescription;
  }

  getAiAlertAnomalyTypeTranslation(type: String): string {
    if(!type) {
      return 'NA';
    }
    if(!this.aiAlertAnomalyTypeTranslationInitialized) {
      this.customTranslateService.get('aiAlertAnomalyTypeCumulativeDuration').subscribe({
        next: (aiAlertAnomalyTypeTranslationInitialized : string) => {
          this.aiAlertAnomalyTypeCumulativeTranslation = aiAlertAnomalyTypeTranslationInitialized;
        }
      });
      this.customTranslateService.get('aiAlertAnomalyTypeInstanceCount').subscribe({
        next: (aiAlertAnomalyTypeInstanceTranslation : string) => {
          this.aiAlertAnomalyTypeInstanceTranslation = aiAlertAnomalyTypeInstanceTranslation;
        }
      });
      this.customTranslateService.get('aiAlertAnomalyTypeInstanceDuration').subscribe({
        next: (aiAlertAnomalyTypeInstanceDuration : string) => {
          this.aiAlertAnomalyTypeInstanceDuration = aiAlertAnomalyTypeInstanceDuration;
        }
      });
      this.aiAlertAnomalyTypeTranslationInitialized = true;
    }

    switch (type.trim()) {
      case AiAlertAnomalyType.TYPE_CUMULATIVE_DURATION:
        return this.aiAlertAnomalyTypeCumulativeTranslation;
      case AiAlertAnomalyType.TYPE_INSTANCE_COUNT:
        return this.aiAlertAnomalyTypeInstanceTranslation;
      case AiAlertAnomalyType.TYPE_INSTANCE_DURATION:
        return this.aiAlertAnomalyTypeInstanceDuration;
      default:
        return 'NA';
    }
  }

  getAiAlertChildAnomalySeverityTypeTranslation(type: String): string {
    if(!type) {
      return 'NA';
    }
    if(!this.aiAlertAnomalySeverityTranslationInitialized) {
      this.customTranslateService.get('aiAlertAnomalySeverityUnKnown').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeverityUnKnown = message;
        }
      });
      this.customTranslateService.get('aiAlertAnomalySeverityNone').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeverityNone = message;
        }
      });
      this.customTranslateService.get('aiAlertAnomalySeverityLow').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeverityLow = message;
        }
      });
      this.customTranslateService.get('aiAlertAnomalySeverityMedium').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeverityMedium = message;
        }
      });
      this.customTranslateService.get('aiAlertAnomalySeverityHigh').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeverityHigh = message;
        }
      });
      this.customTranslateService.get('aiAlertAnomalySeveritySevere').subscribe({
        next:(message) => {
          this.aiAlertAnomalySeveritySevere = message;
        }
      });
      this.aiAlertAnomalySeverityTranslationInitialized = true;
    }

    switch (type.trim()) {
      case AiAlertAnomalySeverity.SEVERITY_UNKNOWN:
        return this.aiAlertAnomalySeverityUnKnown;
      case AiAlertAnomalySeverity.SEVERITY_NONE:
        return this.aiAlertAnomalySeverityNone;
      case AiAlertAnomalySeverity.SEVERITY_LOW:
        return this.aiAlertAnomalySeverityLow;
      case AiAlertAnomalySeverity.SEVERITY_MEDIUM:
        return this.aiAlertAnomalySeverityMedium;
      case AiAlertAnomalySeverity.SEVERITY_HIGH:
        return this.aiAlertAnomalySeverityHigh;
      case AiAlertAnomalySeverity.SEVERITY_SEVERE:
        return this.aiAlertAnomalySeveritySevere;
      default:
        return 'NA';
    }
  }

  getLineNamesForSelectedClients(clientIds: any,  linedataforCurrentUser: Array<Line>): Map<string, string> {
    let results: Map<string, string> = new Map();
    if (clientIds.length === 0) {
      linedataforCurrentUser.forEach((lineData: Line) => {
        results.set(lineData.lineId!.toString(), lineData.lineName!)

      });
    } else {
      clientIds.forEach( (clientId : number)  => {
        linedataforCurrentUser.forEach((lineData: Line) => {
          if (Number(clientId) === lineData.companyId) {
            // results.push(lineData.lineName);
            results.set(lineData.lineId!.toString(), lineData.lineName!)
          }
        });
      });
    }
    return results;
  }

  getClientNamesForUserLines(clientIds: any,  linedataforCurrentUser: Array<Line>): Map<string, string> {
    let results: Map<string, string> = new Map();
    if (clientIds.length === 0) {
      linedataforCurrentUser.forEach((lineData: Line) => {
        results.set(lineData.plant!.companyId!.toString(), lineData.plant!.companyName!)

      });
    } else {
      clientIds.forEach( (clientId : number) => {
        linedataforCurrentUser.forEach((lineData: Line) => {
          if (Number(clientId) === lineData.companyId) {
            // results.push(lineData.lineName);
            results.set(lineData.plant!.companyId!.toString(), lineData.plant!.companyName!)
          }
        });
      });
    }
    return results;
  }

  getPlantNamesForUserLines(clientIds: any,  linedataforCurrentUser: Array<Line>): Map<string, string> {
    let results: Map<string, string> = new Map();
    if (clientIds.length === 0) {
      linedataforCurrentUser.forEach((lineData: Line) => {
        results.set(lineData.plant!.plantId!.toString(), lineData.plant!.plantName!)

      });
    } else {
      clientIds.forEach( (clientId : number) => {
        linedataforCurrentUser.forEach((lineData: Line) => {
          if (Number(clientId) === lineData.companyId) {
            // results.push(lineData.lineName);
            results.set(lineData.plant!.plantId!.toString(), lineData.plant!.plantName!)
          }
        });
      });
    }
    return results;
  }

  getAssetsForGivenLines(linedataforCurrentUser: Array<Line>): Array<any> {
    let assetlist: Array<any> = [];
    linedataforCurrentUser.forEach((lineData: Line) => {
      if (lineData.assetList!.length > 0) {
        lineData.assetList!.forEach((asset: Asset) => {
          if (assetlist.indexOf(asset.uniqueAssetIdThingWorksFK) === -1) {
            assetlist.push(asset.uniqueAssetIdThingWorksFK);
          }
        })
      }
    });
    return assetlist;
  }

  getStatusTranslation(state: AlertStatus): string {
    if(!this.translationInitialized) {

      this.customTranslateService.get('t.alertMonitorStateOpen').subscribe((result) => {
        this.stateOpenTranslation = result;
      });
      this.customTranslateService.get('t.alertMonitorStateAcknowledged').subscribe((result) => {
        this.stateAcknowledgedTranslation = result;
      });
      this.customTranslateService.get('t.alertMonitorStateClosed').subscribe((result) => {
        this.stateClosedTranslation = result;
      });

      this.translationInitialized = true;
    }

    switch (state) {
      case AlertStatus.OPEN:
        return this.stateOpenTranslation;
      case AlertStatus.ACKNOWLEDGED:
        return this.stateAcknowledgedTranslation;
      case AlertStatus.CLOSED:
        return this.stateClosedTranslation;
      default:
        return 'NA';
    }
  }

  calculateDuration(maxPrecisionCount: number, start: string, end?: string): string {
    let endMoment = moment();
    if (end) {
      endMoment = moment(end, this.targetDatetimeFormat);
    }
    let startMoment = moment(start, this.targetDatetimeFormat);
    let result = '';
    let transAgo = '';
    let precisionCount = 0;
    if(endMoment.diff(startMoment, 'months') >=1) {
      maxPrecisionCount = 3;
    }
    if(endMoment.diff(startMoment, 'days') < 1) {
      maxPrecisionCount = 2;
    }
    for (let i = 0; i < this.unitTransTokens.length; i++) {
      let transUnits: string[] = [];
      // translate measurement units.
      transUnits[i] = this.customTranslateService.instant('t.' + this.unitTransTokens[i]);

      // check the difference between the en d of the alert and the start of it.
      if (endMoment.diff(startMoment, this.unitValues[i]) > 0) {
        if (precisionCount > 0) {
          result += ' ';
        }
        result += (endMoment.diff(startMoment, this.unitValues[i]) + ' ' + transUnits[i]);
        startMoment.add(endMoment.diff(startMoment, this.unitValues[i]), this.unitValues[i]);
        precisionCount++;
        if (precisionCount === maxPrecisionCount) {
          break;
        }
      }
    }

    return result;
  }

  populateSourceInfoForAlert(alertData: AlertData): Observable<any> {
    const observables = [];
    observables.push(this.alertCommentRepository.getAlertComments(alertData.alertInstanceId!, alertData.alertRuleId!).pipe(switchMap((comments:Array<AlertComment>)  => {
      if(comments) {
        comments.forEach((comment) => {
          comment.__displayTimestamp = this.customDatesService.convertMillisToUserFormat(comment.commentTimestamp!);
        });
        alertData.commentList = comments;
      }
      return of({});
    })));

    observables.push(this.alertFeedbackRepository.getAlertFeedback(alertData.alertInstanceId!, this.userSessionRepository.getUserSession()!.email!).pipe(switchMap((feedback:AlertFeedback)  => {
      if(feedback) {
        if(feedback.hasOwnProperty('feedback')) {
          alertData.currentFeedback = feedback['feedback'];
        }
      }
      return of({});
    })));

    observables.push(this.remedyRepository.getRemedyByAlertRule(alertData.alertRuleId!).pipe(switchMap((remedy:Remedy)  => {
      if (remedy) {
        alertData.remedy = remedy;
      }
      return of({});
    })));
    if(alertData.asset) {
      let observable = this.assetRepository.getAssetByUniqueAssetIdThingWorksFk(alertData.asset).pipe(switchMap((asset: Asset) => {
        alertData.assetDisplayName = Asset.getFriendlyDisplayName(asset);
        alertData.assetTypeTemplateTag = asset.assetTypeTemplateTag;
        return of({});
      }));
      observables.push(observable);
    }
    if(alertData.lineId) {
      let observable = this.lineRepository.getLine(alertData.lineId).pipe(switchMap((line: Line) => {
        alertData.line = line.lineName!;
        alertData.plant = line.plant!.plantName;
        alertData.client = line.companyName;
        return of({});
      }));
      observables.push(observable);
    }

    if(alertData.ackBy && alertData.ackBy !== 'NA') {
      let observable = this.userRepository.getUser(Number(alertData.ackBy)).pipe(switchMap((user) => {
        alertData.ackUserFirstName = user.firstName;
        alertData.ackUserLastName = user.lastName;

        return of({});
      }));

      // let observable = this.userRepository.getAllUsersWithAccessToClient(alertData.clientId).pipe(switchMap((users: Array<User>) => {
      //   let ackUser = users.find(user => user.userId === Number(alertData.ackBy));
      //   if (ackUser) {
      //     alertData.ackUserFirstName = ackUser.firstName!.toUpperCase();
      //     alertData.ackUserLastName = ackUser.lastName!.toUpperCase();
      //   }
      //   return of({});
      // }));

      observables.push(observable);
    }

    if(observables.length === 0) {
      return of({});
    }

    return forkJoin(observables).pipe(switchMap((result) => {
      if(alertData.asset) {
        alertData.source = alertData.asset;
      }
      else {
        alertData.source = alertData.line;
      }
      return of({});
    }));
  }
}
